import { Box, Button, Flex, HStack, InfoBox, Modal, Text, VStack } from 'components/ui';
import { Checkbox } from 'components/ui/checkbox';
import { useTranslation } from 'hooks/useTranslation';
import { useEffect, useState } from 'react';
import { MdAdd, MdClose, MdDelete, MdEdit, MdExpandLess, MdExpandMore } from 'react-icons/md';
import {
  ChangeType, CompareDocumentationResponse,
  EndpointChange, SelectedEndpointChange,
  serverService
} from 'services/serverService';

interface ServerComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  serverId: string;
  comparisonResult: CompareDocumentationResponse;
  onUpdatesApplied: () => void;
}

const ServerComparisonModal = ({
  isOpen,
  onClose,
  serverId,
  comparisonResult,
  onUpdatesApplied,
}: ServerComparisonModalProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedChanges, setSelectedChanges] = useState<Set<string>>(new Set());
  const [expandedChanges, setExpandedChanges] = useState<Set<string>>(new Set());

  // Reset state when modal opens or comparisonResult changes
  useEffect(() => {
    if (isOpen) {
      // Select all changes by default
      const allChangeKeys = new Set(
        comparisonResult.changes.map(change => getChangeKey(change))
      );
      setSelectedChanges(allChangeKeys);
      setExpandedChanges(new Set());
      setError('');
    }
  }, [isOpen, comparisonResult]);

  // Group changes by type
  const newEndpoints = comparisonResult.changes.filter((c) => c.changeType === ChangeType.New);
  const updatedEndpoints = comparisonResult.changes.filter((c) => c.changeType === ChangeType.Updated);
  const deletedEndpoints = comparisonResult.changes.filter((c) => c.changeType === ChangeType.Deleted);

  const getChangeKey = (change: EndpointChange) => `${change.method}-${change.path}`;

  const handleToggleExpanded = (change: EndpointChange) => {
    const key = getChangeKey(change);
    const newExpanded = new Set(expandedChanges);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedChanges(newExpanded);
  };

  const handleToggleChange = (change: EndpointChange) => {
    const key = getChangeKey(change);
    const newSelected = new Set(selectedChanges);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedChanges(newSelected);
  };

  const handleToggleAll = (changes: EndpointChange[], select: boolean) => {
    const newSelected = new Set(selectedChanges);
    changes.forEach((change) => {
      const key = getChangeKey(change);
      if (select) {
        newSelected.add(key);
      } else {
        newSelected.delete(key);
      }
    });
    setSelectedChanges(newSelected);
  };

  const handleApplyUpdates = async () => {
    if (selectedChanges.size === 0) {
      setError(t('app.servers.documentationComparison.selectAtLeastOne'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const selectedChangesList: SelectedEndpointChange[] = comparisonResult.changes
        .filter((change) => selectedChanges.has(getChangeKey(change)))
        .map((change) => ({
          path: change.path,
          method: change.method,
          changeType: change.changeType,
          existingToolId: change.existingToolId,
        }));

      await serverService.applyDocumentationUpdates(serverId, {
        selectedChanges: selectedChangesList,
      });

      onUpdatesApplied();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || t('app.servers.documentationComparison.errors.applyFailed'));
    } finally {
      setLoading(false);
    }
  };

  const renderEndpointChange = (change: EndpointChange) => {
    const key = getChangeKey(change);
    const isSelected = selectedChanges.has(key);
    const isExpanded = expandedChanges.has(key);

    return (
      <Box
        key={key}
        p="4"
        borderWidth="1px"
        borderRadius="md"
        borderColor={isSelected ? 'blue.500' : 'gray.200'}
        bg={isSelected ? 'blue.50' : 'white'}
        _dark={{
          borderColor: isSelected ? 'blue.400' : 'gray.600',
          bg: isSelected ? 'blue.900' : 'gray.800',
        }}
        cursor="pointer"
        _hover={{ opacity: 0.9 }}
        onClick={() => handleToggleChange(change)}
      >
        <HStack justify="space-between" mb="2">
          <HStack gap="3">
            <Box onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => handleToggleChange(change)}
                colorPalette="blue"
                size="sm"
              />
            </Box>
            <Text fontWeight="semibold" fontSize="sm">
              {change.method} {change.path}
            </Text>
          </HStack>
          <Button
            size="xs"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleExpanded(change);
            }}
          >
            {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
            {isExpanded ? t('app.servers.documentationComparison.hideDetails') : t('app.servers.documentationComparison.showDetails')}
          </Button>
        </HStack>

        {isExpanded && (
          <VStack align="stretch" mt="2" gap="2" pl="6">
            {change.oldDetails && change.newDetails && (
              <>
                <Text fontSize="xs">
                  <Text as="span" fontWeight="bold">{t('app.servers.documentationComparison.oldSummary')} </Text>
                  <Text as="span" color="gray.600" _dark={{ color: 'gray.400' }}>
                    {change.oldDetails.summary || t('app.servers.documentationComparison.na')}
                  </Text>
                </Text>
                <Text fontSize="xs">
                  <Text as="span" fontWeight="bold">{t('app.servers.documentationComparison.newSummary')} </Text>
                  <Text as="span" color="green.600" _dark={{ color: 'green.400' }}>
                    {change.newDetails.summary || t('app.servers.documentationComparison.na')}
                  </Text>
                </Text>
                <Text fontSize="xs">
                  <Text as="span" fontWeight="bold">{t('app.servers.documentationComparison.parametersChanged')} </Text>
                  {t('app.servers.documentationComparison.old')} {change.oldDetails.parameters.length} | {t('app.servers.documentationComparison.new')} {change.newDetails.parameters.length}
                </Text>
              </>
            )}
            {change.newDetails && !change.oldDetails && (
              <>
                <Text fontSize="xs">
                  <Text as="span" fontWeight="bold">{t('app.servers.documentationComparison.summary')} </Text>
                  {change.newDetails.summary || t('app.servers.documentationComparison.na')}
                </Text>
                <Text fontSize="xs">
                  <Text as="span" fontWeight="bold">{t('app.servers.documentationComparison.parameters')} </Text>
                  {change.newDetails.parameters.length}
                </Text>
              </>
            )}
            {change.oldDetails && !change.newDetails && (
              <Text fontSize="xs">
                <Text as="span" fontWeight="bold">{t('app.servers.documentationComparison.summary')} </Text>
                {change.oldDetails.summary || t('app.servers.documentationComparison.na')}
              </Text>
            )}
          </VStack>
        )}
      </Box>
    );
  };

  const renderSection = (
    title: string,
    icon: React.ReactNode,
    changes: EndpointChange[],
    colorScheme: string
  ) => {
    if (changes.length === 0) return null;

    const allSelected = changes.every((c) => selectedChanges.has(getChangeKey(c)));
    const someSelected = changes.some((c) => selectedChanges.has(getChangeKey(c)));

    return (
      <Box mb="6">
        <HStack justify="space-between" mb="3">
          <HStack>
            {icon}
            <Text fontSize="lg" fontWeight="bold">
              {title} ({changes.length})
            </Text>
          </HStack>
          <Button
            size="sm"
            variant="ghost"
            colorPalette={colorScheme}
            onClick={() => handleToggleAll(changes, !allSelected)}
          >
            {allSelected ? t('app.servers.documentationComparison.deselectAll') : t('app.servers.documentationComparison.selectAll')}
          </Button>
        </HStack>
        <VStack align="stretch" gap="2">
          {changes.map(renderEndpointChange)}
        </VStack>
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={() => !loading && onClose()} size="xl">
      <Modal.Header>
        <Flex justify="space-between" align="center" w="full">
          <Text fontSize="xl" fontWeight="bold">
            {t('app.servers.documentationComparison.modalTitle')}
          </Text>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={loading}>
            <MdClose />
          </Button>
        </Flex>
      </Modal.Header>

      <Modal.Body>
        <VStack align="stretch" gap="4">
          <Box
            p="3"
            bg="yellow.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor="yellow.200"
            _dark={{ bg: 'yellow.900', borderColor: 'yellow.700' }}
          >
            <Text fontSize="sm" fontWeight="medium">
              {t('app.servers.documentationComparison.noteText')}
            </Text>
            <Text fontSize="xs" mt="1" color="gray.600" _dark={{ color: 'gray.400' }}>
              {t('app.servers.documentationComparison.currentVersion')} {comparisonResult.currentSpecVersion} → {t('app.servers.documentationComparison.newVersion')}{' '}
              {comparisonResult.newSpecVersion}
            </Text>
          </Box>

          {error && (
            <InfoBox variant="error">
              <Text fontSize="sm" color="red.600" _dark={{ color: 'red.400' }}>
                {error}
              </Text>
            </InfoBox>
          )}

          {renderSection(t('app.servers.documentationComparison.newEndpoints'), <MdAdd color="green" />, newEndpoints, 'green')}
          {renderSection(t('app.servers.documentationComparison.updatedEndpoints'), <MdEdit color="blue" />, updatedEndpoints, 'blue')}
          {renderSection(t('app.servers.documentationComparison.deletedEndpoints'), <MdDelete color="red" />, deletedEndpoints, 'red')}

          {comparisonResult.changes.length === 0 && (
            <Box p="6" textAlign="center">
              <Text color="gray.500">{t('app.servers.documentationComparison.noChangesDetected')}</Text>
            </Box>
          )}
        </VStack>
      </Modal.Body>

      <Modal.Footer>
        <HStack justify="space-between" w="full">
          <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
            {t('app.servers.documentationComparison.changesSelected', { selected: selectedChanges.size, total: comparisonResult.changes.length })}
          </Text>
          <HStack>
            <Button variant="outline" onClick={onClose} disabled={loading}>
              {t('app.servers.documentationComparison.cancel')}
            </Button>
            <Button
              colorPalette="blue"
              onClick={handleApplyUpdates}
              loading={loading}
              disabled={selectedChanges.size === 0}
            >
              {t('app.servers.documentationComparison.updateServer')}
            </Button>
          </HStack>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

export default ServerComparisonModal;
